/**
 * Mock-only in-memory donor used by the in-app signup demo.
 * In mock mode, `/signup` makes `GET /me` return 404 until provisioned; the SPA then calls `POST /me`, which creates (or resumes) this onboarding donor.
 * Persisted in sessionStorage so a soft or hard navigation mid-signup does not
 * lose landingPage / wipe the in-progress donor (Mirage restarts on full reload).
 */
import { DateTime } from 'luxon'

const ONBOARDING_STORAGE_KEY = 'swiftaidMockOnboarding'

type OnboardingMe = {
  landingPage: number
  emailAddress: string
  details: {
    firstname: string
    lastname: string
    addressLine1: string
    addressLine2: string
    city: string
    county: string
    postcode: string
    country: string
  }
  accounts: Array<{
    type: string
    id: string
    created: string
    updated: string
  }>
  dashboardSeen: boolean
  dashboardFirstSeenAt: string | null
  pendingEmailVerifications: unknown[]
}

let onboarding: OnboardingMe | null = readStoredOnboarding()

function readStoredOnboarding(): OnboardingMe | null {
  try {
    const raw = sessionStorage.getItem(ONBOARDING_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as OnboardingMe
    if (
      parsed &&
      typeof parsed.landingPage === 'number' &&
      typeof parsed.emailAddress === 'string'
    ) {
      return parsed
    }
  } catch {
    // ignore invalid / unavailable storage
  }
  return null
}

function persistOnboarding(): void {
  try {
    if (onboarding) {
      sessionStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(onboarding))
    } else {
      sessionStorage.removeItem(ONBOARDING_STORAGE_KEY)
    }
  } catch {
    // ignore quota / private mode
  }
}

export function isMockOnboardingActive(): boolean {
  return onboarding !== null
}

export function startMockOnboarding(email = 'new.donor@example.com'): OnboardingMe {
  const now = DateTime.utc().toISO()
  onboarding = {
    landingPage: 0,
    emailAddress: email,
    details: {
      firstname: '',
      lastname: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      county: '',
      postcode: '',
      country: 'United Kingdom',
    },
    accounts: [
      {
        type: 'emailAddress',
        id: email,
        created: now,
        updated: now,
      },
    ],
    dashboardSeen: false,
    dashboardFirstSeenAt: null,
    pendingEmailVerifications: [],
  }
  persistOnboarding()
  return onboarding
}

export function getMockOnboarding(): OnboardingMe | null {
  return onboarding
}

export function updateMockOnboardingDetails(details: OnboardingMe['details']): void {
  if (!onboarding) return
  onboarding = {
    ...onboarding,
    details: { ...onboarding.details, ...details },
    landingPage: 1,
  }
  persistOnboarding()
}

export function completeMockOnboarding(): void {
  if (!onboarding) return
  // Keep the onboarding donor so the demo doesn’t snap back to another persona;
  // mark setup complete so guards allow the dashboard.
  onboarding = {
    ...onboarding,
    landingPage: 100,
  }
  persistOnboarding()
}

export function clearMockOnboarding(): void {
  onboarding = null
  persistOnboarding()
}
